import { createContext, useContext } from "react";

export interface IconProps extends React.SVGProps<SVGSVGElement> {
  color?: string;
  variant?: "base" | "xs" | "sm" | "md" | "lg";
  title?: string;
}

const IconSubtleContext = createContext(false);
export const IconSubtleProvider = IconSubtleContext.Provider;
const useIsSubtle = () => useContext(IconSubtleContext);

const SUBTLE_COLOR = "#595E63";

const iconSize = (variant: IconProps["variant"] = "base"): number => {
  if (variant === "xs") return 8;
  if (variant === "sm") return 16;
  if (variant === "md") return 20;
  if (variant === "lg") return 32;
  return 24;
};

const IconStrokeBase = ({
  children,
  color,
  variant = "base",
  title = "icon",
  ...rest
}: IconProps & { title: string }) => {
  const subtle = useIsSubtle();
  const effectiveColor = color ?? (subtle ? SUBTLE_COLOR : "#111920");
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={iconSize(variant)}
      height={iconSize(variant)}
      stroke={effectiveColor}
      fill="none"
      strokeWidth={subtle ? 1.5 : 2}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      <title>{title}</title>
      {children}
    </svg>
  );
};

const IconSubtleBase = ({
  children,
  color,
  variant = "base",
  title = "icon",
  viewBox = "0 0 20 20",
  strokeWidth = 1.5,
  ...rest
}: IconProps & {
  title: string;
  viewBox?: string;
}) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox}
      width={iconSize(variant)}
      height={iconSize(variant)}
      stroke={color ?? SUBTLE_COLOR}
      fill="none"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      <title>{title}</title>
      {children}
    </svg>
  );
};

export const IconArrowRight = (props: IconProps) => {
  return (
    <IconStrokeBase title="Next" {...props}>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </IconStrokeBase>
  );
};

export const IconArrowLeft = (props: IconProps) => {
  return (
    <IconStrokeBase title="Prev" {...props}>
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </IconStrokeBase>
  );
};

export const IconEdit = (props: IconProps) => {
  return (
    <IconStrokeBase title="Edit" {...props}>
      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
    </IconStrokeBase>
  );
};

export const IconChevronUp = (props: IconProps) => {
  return (
    <IconStrokeBase title="Up" {...props}>
      <polyline points="18 15 12 9 6 15" />
    </IconStrokeBase>
  );
};

export const IconChevronRight = (props: IconProps) => {
  return (
    <IconStrokeBase title="Right" {...props}>
      <polyline points="9 18 15 12 9 6" />
    </IconStrokeBase>
  );
};

export const IconChevronLeft = (props: IconProps) => {
  return (
    <IconStrokeBase title="Left" {...props}>
      <polyline points="15 18 9 12 15 6" />
    </IconStrokeBase>
  );
};

export const IconChevronDown = (props: IconProps) => {
  return (
    <IconStrokeBase title="Down" {...props}>
      <polyline points="6 9 12 15 18 9" />
    </IconStrokeBase>
  );
};

export const IconCylinder = (props: IconProps) => {
  if (useIsSubtle()) {
    return (
      <IconSubtleBase title="Databases" {...props}>
        <path d="M17.4038 4.95192C17.4038 6.99628 14.0887 8.65385 9.99998 8.65385C5.91126 8.65385 2.59613 6.99628 2.59613 4.95192M17.4038 4.95192C17.4038 2.90756 14.0887 1.25 9.99998 1.25C5.91126 1.25 2.59613 2.90756 2.59613 4.95192M17.4038 4.95192V15.0481C17.4038 17.0924 14.0887 18.75 9.99998 18.75C5.91126 18.75 2.59613 17.0924 2.59613 15.0481V4.95192M17.4038 4.95192V8.31731M2.59613 4.95192V8.31731M17.4038 8.31731V11.6827C17.4038 13.7271 14.0887 15.3846 9.99998 15.3846C5.91126 15.3846 2.59613 13.7271 2.59613 11.6827V8.31731M17.4038 8.31731C17.4038 10.3617 14.0887 12.0192 9.99998 12.0192C5.91126 12.0192 2.59613 10.3617 2.59613 8.31731" />
      </IconSubtleBase>
    );
  }
  return (
    <IconStrokeBase title="Databases" {...props}>
      <path d="M20.8846 5.94231C20.8846 8.39554 16.9065 10.3846 12 10.3846C7.09354 10.3846 3.11538 8.39554 3.11538 5.94231M20.8846 5.94231C20.8846 3.48908 16.9065 1.5 12 1.5C7.09354 1.5 3.11538 3.48908 3.11538 5.94231M20.8846 5.94231V18.0577C20.8846 20.5109 16.9065 22.5 12 22.5C7.09354 22.5 3.11538 20.5109 3.11538 18.0577V5.94231M20.8846 5.94231V9.98077M3.11538 5.94231V9.98077M20.8846 9.98077V14.0192C20.8846 16.4725 16.9065 18.4615 12 18.4615C7.09354 18.4615 3.11538 16.4725 3.11538 14.0192V9.98077M20.8846 9.98077C20.8846 12.434 16.9065 14.4231 12 14.4231C7.09354 14.4231 3.11538 12.434 3.11538 9.98077" />
    </IconStrokeBase>
  );
};

export const IconTrash = (props: IconProps) => {
  return (
    <IconStrokeBase title="Delete" {...props}>
      <path d="M10 11V17M14 11V17M4 7H20M19 7L18.133 19.142C18.0971 19.6466 17.8713 20.1188 17.5011 20.4636C17.1309 20.8083 16.6439 21 16.138 21H7.862C7.35614 21 6.86907 20.8083 6.49889 20.4636C6.1287 20.1188 5.90292 19.6466 5.867 19.142L5 7H19ZM15 7V4C15 3.73478 14.8946 3.48043 14.7071 3.29289C14.5196 3.10536 14.2652 3 14 3H10C9.73478 3 9.48043 3.10536 9.29289 3.29289C9.10536 3.48043 9 3.73478 9 4V7H15Z" />
    </IconStrokeBase>
  );
};

export const IconBox = (props: IconProps) => {
  if (useIsSubtle()) {
    return (
      <IconSubtleBase title="Apps" {...props}>
        <path d="M18.4615 5.76927L9.99999 0.833374L1.53845 5.76927M18.4615 5.76927L9.99999 10.7052M18.4615 5.76927V14.2308L9.99999 19.1667M1.53845 5.76927L9.99999 10.7052M1.53845 5.76927V14.2308L9.99999 19.1667M9.99999 10.7052V19.1667" />
      </IconSubtleBase>
    );
  }
  return (
    <IconStrokeBase title="Apps" {...props}>
      <path d="M22.1538 6.92308L12 1L1.84615 6.92308M22.1538 6.92308L12 12.8462M22.1538 6.92308V17.0769L12 23M1.84615 6.92308L12 12.8462M1.84615 6.92308V17.0769L12 23M12 12.8462V23" />
    </IconStrokeBase>
  );
};

export const IconSettings = (props: IconProps) => {
  return (
    <IconStrokeBase title="Settings" {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </IconStrokeBase>
  );
};

export const IconUserCircle = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Profile" {...props}>
      <path d="M17.982 18.725C17.2833 17.7999 16.3792 17.0496 15.3412 16.5334C14.3031 16.0171 13.1594 15.7489 12 15.75C10.8406 15.7489 9.69688 16.0171 8.65882 16.5334C7.62075 17.0496 6.71674 17.7999 6.018 18.725M17.981 18.725C19.3445 17.5122 20.3071 15.9136 20.7412 14.1411C21.1752 12.3686 21.0603 10.5061 20.4115 8.80048C19.7627 7.09487 18.6107 5.62678 17.1084 4.5909C15.6061 3.55502 13.8243 3.00029 11.9995 3.00029C10.1747 3.00029 8.39293 3.55502 6.8906 4.5909C5.38828 5.62678 4.23633 7.09487 3.58753 8.80048C2.93874 10.5061 2.82375 12.3686 3.25782 14.1411C3.69189 15.9136 4.6545 17.5122 6.018 18.725M17.981 18.725C16.335 20.1932 14.2056 21.0031 12 21C9.79403 21.0034 7.66424 20.1934 6.018 18.725M15 9.75C15 10.5456 14.6839 11.3087 14.1213 11.8713C13.5587 12.4339 12.7956 12.75 12 12.75C11.2044 12.75 10.4413 12.4339 9.87868 11.8713C9.31607 11.3087 9 10.5456 9 9.75C9 8.95435 9.31607 8.19129 9.87868 7.62868C10.4413 7.06607 11.2044 6.75 12 6.75C12.7956 6.75 13.5587 7.06607 14.1213 7.62868C14.6839 8.19129 15 8.95435 15 9.75V9.75Z" />
    </IconStrokeBase>
  );
};

export const IconSearch = (props: IconProps) => {
  return (
    <IconStrokeBase title="Search" {...props}>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </IconStrokeBase>
  );
};

export const IconCheck = (props: IconProps) => {
  return (
    <IconStrokeBase title="Check" {...props}>
      <polyline points="20 6 9 17 4 12" />
    </IconStrokeBase>
  );
};

export const IconCheckCircle = (props: IconProps) => {
  return (
    <IconStrokeBase title="Check" {...props}>
      <path d="M8.17316 13L11.1667 15.8268L15.2612 8.5M22 12C22 13.3132 21.7413 14.6136 21.2388 15.8268C20.7362 17.0401 19.9996 18.1425 19.0711 19.0711C18.1425 19.9997 17.0401 20.7362 15.8268 21.2388C14.6136 21.7413 13.3132 22 12 22C10.6868 22 9.38642 21.7413 8.17316 21.2388C6.95991 20.7362 5.85751 19.9997 4.92893 19.0711C4.00034 18.1425 3.26375 17.0401 2.7612 15.8268C2.25865 14.6136 2 13.3132 2 12C2 9.34784 3.05356 6.8043 4.92893 4.92893C6.80429 3.05357 9.34783 2 12 2C14.6522 2 17.1957 3.05357 19.0711 4.92893C20.9464 6.8043 22 9.34784 22 12Z" />
    </IconStrokeBase>
  );
};

export const IconCheckCircleAlt = (props: IconProps) => {
  return (
    <IconStrokeBase title="Check Circle Alt" {...props}>
      <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999M22 3.99999L12 14.01L9.00001 11.01" />
    </IconStrokeBase>
  );
};

export const IconExclamationCircle = (props: IconProps) => {
  return <IconAlertCircle {...props} />;
};

export const IconMinusCircle = (props: IconProps) => {
  return (
    <IconStrokeBase title="Minus" {...props}>
      <circle cx="12" cy="12" r="10" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </IconStrokeBase>
  );
};

export const IconPlusCircle = (props: IconProps) => {
  return (
    <IconStrokeBase title="New" {...props}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="16" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </IconStrokeBase>
  );
};

export const IconX = (props: IconProps) => {
  return (
    <IconStrokeBase title="Remove" {...props}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </IconStrokeBase>
  );
};

export const IconXCircle = (props: IconProps) => {
  return (
    <IconStrokeBase title="Remove" {...props}>
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </IconStrokeBase>
  );
};

export const IconAlertCircle = (props: IconProps) => {
  return (
    <IconStrokeBase title="Alert" {...props}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </IconStrokeBase>
  );
};

export const IconAlertTriangle = (props: IconProps) => {
  return (
    <IconStrokeBase title="Alert" {...props}>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </IconStrokeBase>
  );
};

export const IconLayers = (props: IconProps) => {
  if (useIsSubtle()) {
    return (
      <IconSubtleBase
        title="Stacks"
        strokeLinecap="square"
        strokeLinejoin="miter"
        {...props}
      >
        <path d="M2.42426 6.21208L10 1.66663L17.5758 6.21208L10 10.7575L2.42426 6.21208Z" />
        <path d="M17.5758 10L10 14.5455L2.42426 10" />
        <path d="M17.5758 13.7878L10 18.3333L2.42426 13.7878" />
      </IconSubtleBase>
    );
  }
  return (
    <IconStrokeBase title="Stacks" {...props}>
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </IconStrokeBase>
  );
};

export const IconLogout = (props: IconProps) => {
  return (
    <IconStrokeBase title="Logout" {...props}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </IconStrokeBase>
  );
};

export const IconGitBranch = (props: IconProps) => {
  return (
    <IconStrokeBase title="Git Branch" {...props}>
      <line x1="6" y1="3" x2="6" y2="15" />
      <circle cx="18" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <path d="M18 9a9 9 0 0 1-9 9" />
    </IconStrokeBase>
  );
};

export const IconInfo = (props: IconProps) => {
  return (
    <IconStrokeBase title="Info" {...props}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </IconStrokeBase>
  );
};

export const IconCreditCard = (props: IconProps) => {
  return (
    <IconStrokeBase title="Billing" {...props}>
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
      <line x1="1" y1="10" x2="23" y2="10" />
    </IconStrokeBase>
  );
};

export const IconGlobe = (props: IconProps) => {
  if (useIsSubtle()) {
    return (
      <IconSubtleBase title="Environments" {...props}>
        <path d="M10 18.3333C11.8472 18.3332 13.6421 17.7196 15.1028 16.5889C16.5635 15.4581 17.6074 13.8743 18.0704 12.0861M10 18.3333C8.15281 18.3332 6.35793 17.7196 4.89722 16.5889C3.43651 15.4581 2.39269 13.8743 1.92965 12.0861M10 18.3333C12.301 18.3333 14.1667 14.6018 14.1667 9.99996C14.1667 5.39811 12.301 1.66663 10 1.66663M10 18.3333C7.6991 18.3333 5.83336 14.6018 5.83336 9.99996C5.83336 5.39811 7.6991 1.66663 10 1.66663M10 1.66663C11.478 1.66601 12.9296 2.05862 14.2058 2.80419C15.4819 3.54975 16.5368 4.62141 17.2621 5.90922M10 1.66663C8.52202 1.66601 7.07047 2.05862 5.79429 2.80419C4.51811 3.54975 3.46327 4.62141 2.73799 5.90922M17.2621 5.90922C15.246 7.65545 12.6672 8.61491 10 8.61107C7.2241 8.61107 4.68521 7.59255 2.73799 5.90922M17.2621 5.90922C17.9666 7.15736 18.3356 8.56673 18.3334 9.99996C18.3334 10.7203 18.2417 11.4194 18.0704 12.0861M18.0704 12.0861C15.6011 13.4549 12.8234 14.1711 10 14.1666C7.07225 14.1666 4.32132 13.412 1.92965 12.0861M1.92965 12.0861C1.75442 11.4046 1.66606 10.7036 1.66669 9.99996C1.66669 8.51385 2.05558 7.11755 2.73799 5.90922" />
      </IconSubtleBase>
    );
  }
  return (
    <IconStrokeBase title="Environments" {...props}>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </IconStrokeBase>
  );
};

export const IconEllipsis = (props: IconProps) => {
  return (
    <IconStrokeBase title="More" {...props}>
      <circle cx="12" cy="12" r="1" />
      <circle cx="19" cy="12" r="1" />
      <circle cx="5" cy="12" r="1" />
    </IconStrokeBase>
  );
};

export const IconExternalLink = (props: IconProps) => {
  return (
    <IconStrokeBase title="External Link" {...props}>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </IconStrokeBase>
  );
};

export const IconCopy = (props: IconProps) => {
  return (
    <IconStrokeBase title="Copy" {...props}>
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </IconStrokeBase>
  );
};

export const IconDownload = (props: IconProps) => {
  return (
    <IconStrokeBase title="Download" {...props}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </IconStrokeBase>
  );
};

export const IconThumbsUp = (props: IconProps) => {
  return (
    <IconStrokeBase title="Thumbs Up" {...props}>
      <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
    </IconStrokeBase>
  );
};

export const IconRefresh = (props: IconProps) => {
  return (
    <IconStrokeBase title="Refresh" {...props}>
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </IconStrokeBase>
  );
};

// Figma: icons / rollback (node 2047-1377)
export const IconRollback = (props: IconProps) => {
  return (
    <IconStrokeBase
      title="Rollback"
      viewBox="0 0 20 20"
      strokeWidth={1.5}
      {...props}
    >
      <path d="M5.83325 8.33337H0.833252V3.33337M0.833252 8.33337L4.69992 4.70004C5.85068 3.55122 7.34363 2.80751 8.95382 2.58097C10.564 2.35442 12.2042 2.65732 13.6273 3.44401C15.0504 4.2307 16.1792 5.45857 16.8438 6.94262C17.5083 8.42667 17.6726 10.0865 17.3118 11.672C16.951 13.2575 16.0846 14.6828 14.8434 15.7332C13.6021 16.7835 12.053 17.402 10.4297 17.4954C8.80632 17.5889 7.19657 17.1522 5.84297 16.2512C4.48937 15.3502 3.46525 14.0337 2.92492 12.5" />
    </IconStrokeBase>
  );
};

export const IconHeart = (props: IconProps) => {
  return (
    <IconStrokeBase title="Activity" {...props}>
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </IconStrokeBase>
  );
};

export const IconCloud = (props: IconProps) => {
  return (
    <IconStrokeBase title="Deployments" {...props}>
      <path d="M12.006 16.6069V8.97256M12.006 8.97256L15.3908 12.3656M12.006 8.97256L8.62126 12.3656M6.08268 20C4.87481 20.0013 3.70603 19.5709 2.78625 18.7861C1.86647 18.0012 1.25594 16.9134 1.06431 15.7179C0.872685 14.5224 1.11251 13.2975 1.74072 12.2634C2.36892 11.2292 3.34435 10.4534 4.49184 10.0753C4.19703 8.56106 4.50172 6.99124 5.3414 5.69827C6.18108 4.40531 7.48981 3.49073 8.99044 3.1482C10.4911 2.80568 12.0658 3.06211 13.3811 3.86319C14.6964 4.66428 15.6491 5.9471 16.0373 7.44003C16.6375 7.24433 17.2804 7.22079 17.8933 7.37205C18.5062 7.52331 19.0647 7.84335 19.5057 8.29603C19.9467 8.7487 20.2527 9.31597 20.389 9.93377C20.5254 10.5516 20.4867 11.1953 20.2773 11.7922C21.2009 12.1459 21.972 12.8125 22.4566 13.6764C22.9412 14.5403 23.1089 15.5471 22.9305 16.522C22.7522 17.4969 22.239 18.3784 21.4801 19.0137C20.7211 19.649 19.7643 19.9979 18.7756 20H6.08268Z" />
    </IconStrokeBase>
  );
};

export const IconHamburger = (props: IconProps) => {
  return (
    <IconStrokeBase title="Menu" {...props}>
      <path d="M3.75 6.75H20.25M3.75 12H20.25M3.75 17.25H20.25" />
    </IconStrokeBase>
  );
};

export const IconWorkplace = (props: IconProps) => {
  return (
    <IconStrokeBase title="Organization" {...props}>
      <path d="M3.75 21H20.25M4.5 3H19.5M5.25 3V21M18.75 3V21M9 6.75H10.5M9 9.75H10.5M9 12.75H10.5M13.5 6.75H15M13.5 9.75H15M13.5 12.75H15M9 21V17.625C9 17.004 9.504 16.5 10.125 16.5H13.875C14.496 16.5 15 17.004 15 17.625V21" />
    </IconStrokeBase>
  );
};

export const IconMetrics = (props: IconProps) => {
  return (
    <IconStrokeBase title="Metrics" {...props}>
      <path d="M16 8V16M12 11V16M8 14V16M6 20H18C18.5304 20 19.0391 19.7893 19.4142 19.4142C19.7893 19.0391 20 18.5304 20 18V6C20 5.46957 19.7893 4.96086 19.4142 4.58579C19.0391 4.21071 18.5304 4 18 4H6C5.46957 4 4.96086 4.21071 4.58579 4.58579C4.21071 4.96086 4 5.46957 4 6V18C4 18.5304 4.21071 19.0391 4.58579 19.4142C4.96086 19.7893 5.46957 20 6 20Z" />
    </IconStrokeBase>
  );
};

export const IconShield = (props: IconProps) => {
  if (useIsSubtle()) {
    return (
      <IconSubtleBase title="Security & Compliance" {...props}>
        <path d="M7.22224 10.2045L9.0741 12.0901L12.7778 8.31896M17.9796 4.53271C15.052 4.69102 12.1822 3.66026 10 1.66663C7.81786 3.66026 4.94808 4.69102 2.02039 4.53271C1.78478 5.46147 1.66593 6.41693 1.66669 7.37617C1.66669 12.6473 5.20743 17.0775 10 18.3333C14.7926 17.0775 18.3334 12.6483 18.3334 7.37617C18.3334 6.39378 18.2102 5.44156 17.9796 4.53271Z" />
      </IconSubtleBase>
    );
  }
  return (
    <IconStrokeBase title="Security & Compliance" {...props}>
      <path d="M8.66667 12.2455L10.8889 14.5082L15.3333 9.9828M21.5756 5.4393C18.0623 5.62927 14.6186 4.39237 12 2C9.38141 4.39237 5.93767 5.62927 2.42445 5.4393C2.14171 6.55381 1.99909 7.70036 2 8.85145C2 15.1768 6.24889 20.493 12 22C17.7511 20.493 22 15.178 22 8.85145C22 7.67259 21.8522 6.52992 21.5756 5.4393Z" />
    </IconStrokeBase>
  );
};

export const IconEndpoint = ({
  color,
  variant = "base",
  ...rest
}: IconProps) => {
  if (useIsSubtle()) {
    const stroke = color ?? SUBTLE_COLOR;
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        width={iconSize(variant)}
        height={iconSize(variant)}
        fill="none"
        {...rest}
      >
        <title>Endpoints</title>
        <path
          d="M14.3091 16.2334C14.5784 16.6676 14.9511 17.0295 15.395 17.2842C13.9304 18.4983 12.0516 19.2304 10.0005 19.2305L9.56494 19.2197C7.68267 19.1244 5.96285 18.4137 4.60205 17.2852C4.66824 17.2473 4.73437 17.2095 4.79736 17.167C5.12472 16.9458 5.40725 16.6633 5.62842 16.3359C5.65072 16.3029 5.66891 16.2673 5.68994 16.2334C6.87571 17.1699 8.37221 17.7305 10.0005 17.7305C11.6283 17.7304 13.1237 17.1695 14.3091 16.2334ZM6.9292 2.88574C6.92532 2.949 6.92334 3.01291 6.92334 3.07715C6.92336 3.54598 7.0307 3.98905 7.21826 4.38672C4.75831 5.46053 3.03863 7.91405 3.03857 10.7695C3.03859 11.0293 3.05381 11.2857 3.08154 11.5381C3.08001 11.5381 3.07819 11.5381 3.07666 11.5381C2.65198 11.5381 2.2474 11.6246 1.87939 11.7803C1.78919 11.8184 1.70129 11.861 1.61572 11.9072C1.58459 11.6757 1.56132 11.4412 1.54932 11.2041L1.53857 10.7695C1.53864 7.18017 3.77488 4.11551 6.9292 2.88574ZM13.0698 2.88477C16.2247 4.11422 18.4614 7.17963 18.4614 10.7695L18.4507 11.2041C18.4387 11.4409 18.4144 11.675 18.3833 11.9062C17.9489 11.6716 17.4517 11.5381 16.9233 11.5381C16.9215 11.5381 16.9194 11.5381 16.9175 11.5381C16.9452 11.2857 16.9614 11.0293 16.9614 10.7695C16.9614 7.91361 15.2411 5.46028 12.7808 4.38672C12.9684 3.98895 13.0766 3.54614 13.0767 3.07715C13.0767 3.01256 13.0737 2.94837 13.0698 2.88574Z"
          fill={stroke}
        />
        <circle
          cx="10"
          cy="3.07692"
          r="2.32692"
          stroke={stroke}
          strokeWidth="1.5"
        />
        <circle
          cx="16.9231"
          cy="14.6154"
          r="2.32692"
          stroke={stroke}
          strokeWidth="1.5"
        />
        <circle
          cx="3.07692"
          cy="14.6154"
          r="2.32692"
          stroke={stroke}
          strokeWidth="1.5"
        />
      </svg>
    );
  }
  return (
    <IconStrokeBase title="Endpoints" color={color} variant={variant} {...rest}>
      <path d="M21.1538 12.9231C21.1538 17.9786 17.0555 22.0769 12 22.0769C6.94447 22.0769 2.84615 17.9786 2.84615 12.9231C2.84615 7.86754 6.94447 3.76923 12 3.76923C17.0555 3.76923 21.1538 7.86754 21.1538 12.9231Z" />
      <circle cx="12" cy="3.69231" r="2.69231" fill="white" />
      <circle cx="20.3077" cy="17.5385" r="2.69231" fill="white" />
      <circle cx="3.69231" cy="17.5385" r="2.69231" fill="white" />
    </IconStrokeBase>
  );
};

export const IconService = (props: IconProps) => {
  if (useIsSubtle()) {
    return (
      <IconSubtleBase title="Services" {...props}>
        <path d="M8.63636 18.6364L1.36364 11.3636C1.00198 11.002 0.798798 10.5115 0.798799 10C0.798799 9.48854 1.00198 8.99802 1.36363 8.63637L8.63636 1.36364C8.99802 1.00198 9.48854 0.7988 10 0.7988C10.5115 0.7988 11.002 1.00198 11.3636 1.36364L18.6364 8.63637C18.998 8.99802 19.2012 9.48854 19.2012 10C19.2012 10.5115 18.998 11.002 18.6364 11.3636L11.3636 18.6364C11.002 18.998 10.5115 19.2012 10 19.2012C9.48854 19.2012 8.99802 18.998 8.63636 18.6364Z" />
        <path d="M11.8454 7.68188L14.5454 9.95461L11.8454 12.2273M8.15453 12.2273L5.45453 9.95461L8.15453 7.68188" />
      </IconSubtleBase>
    );
  }
  return (
    <IconStrokeBase title="Services" {...props}>
      <path d="M10.3636 22.3636L1.63637 13.6364C1.20238 13.2024 0.958566 12.6138 0.958566 12C0.958567 11.3862 1.20238 10.7976 1.63637 10.3636L10.3636 1.63636C10.7976 1.20237 11.3863 0.95856 12 0.95856C12.6138 0.95856 13.2024 1.20237 13.6364 1.63636L22.3636 10.3636C22.7976 10.7976 23.0414 11.3862 23.0414 12C23.0414 12.6138 22.7976 13.2024 22.3636 13.6364L13.6364 22.3636C13.2024 22.7976 12.6138 23.0414 12 23.0414C11.3863 23.0414 10.7976 22.7976 10.3636 22.3636Z" />
      <path d="M14.2145 9.2182L17.4545 11.9455L14.2145 14.6727M9.78546 14.6727L6.54546 11.9455L9.78546 9.2182" />
    </IconStrokeBase>
  );
};

export const IconCertificate = (props: IconProps) => {
  return (
    <IconStrokeBase title="Certificates" {...props}>
      <path d="M12 15C15.866 15 19 11.866 19 8C19 4.13401 15.866 1 12 1C8.13401 1 5 4.13401 5 8C5 11.866 8.13401 15 12 15Z" />
      <path d="M8.21 13.89L7 23L12 20L17 23L15.79 13.88" />
    </IconStrokeBase>
  );
};

export const IconKey = (props: IconProps) => {
  if (useIsSubtle()) {
    return (
      <IconSubtleBase title="LLM Gateway" viewBox="0 0 18 18" {...props}>
        <path d="M12 3.24569C12.663 3.24569 13.2989 3.50909 13.7678 3.97793C14.2366 4.44677 14.5 5.08265 14.5 5.74569M17 5.74569C17.0001 6.47496 16.8407 7.19541 16.5329 7.85655C16.2252 8.51769 15.7765 9.1035 15.2184 9.57291C14.6603 10.0423 14.0063 10.384 13.3022 10.5739C12.5981 10.7638 11.861 10.7974 11.1425 10.6724C10.6733 10.5915 10.1767 10.694 9.84 11.0307L7.625 13.2457H5.75V15.1207H3.875V16.9957H0.75V14.6474C0.75 14.1499 0.9475 13.6724 1.29917 13.3215L6.715 7.90569C7.05167 7.56903 7.15417 7.07236 7.07333 6.60319C6.95516 5.92083 6.98003 5.22136 7.14636 4.54911C7.31268 3.87687 7.61684 3.2465 8.03956 2.69796C8.46228 2.14943 8.99434 1.69469 9.60202 1.36256C10.2097 1.03043 10.8797 0.828152 11.5697 0.768552C12.2596 0.708952 12.9544 0.793326 13.6101 1.01633C14.2657 1.23933 14.8679 1.59609 15.3784 2.06401C15.8889 2.53193 16.2967 3.1008 16.5758 3.73457C16.855 4.36833 16.9994 5.05318 17 5.74569V5.74569Z" />
      </IconSubtleBase>
    );
  }
  return (
    <IconStrokeBase title="CLI SSO Token" {...props}>
      <path d="M15.75 5.25C16.5456 5.25 17.3087 5.56607 17.8713 6.12868C18.4339 6.69129 18.75 7.45435 18.75 8.25M21.75 8.25C21.7501 9.12511 21.5588 9.98966 21.1895 10.783C20.8202 11.5764 20.2818 12.2794 19.6121 12.8427C18.9424 13.406 18.1575 13.8159 17.3126 14.0438C16.4677 14.2718 15.5831 14.3121 14.721 14.162C14.158 14.065 13.562 14.188 13.158 14.592L10.5 17.25H8.25V19.5H6V21.75H2.25V18.932C2.25 18.335 2.487 17.762 2.909 17.341L9.408 10.842C9.812 10.438 9.935 9.842 9.838 9.279C9.6962 8.46017 9.72604 7.6208 9.92563 6.81411C10.1252 6.00741 10.4902 5.25097 10.9975 4.59273C11.5047 3.93448 12.1432 3.38879 12.8724 2.99024C13.6016 2.59168 14.4057 2.34895 15.2336 2.27743C16.0616 2.20591 16.8953 2.30716 17.6821 2.57476C18.4688 2.84236 19.1914 3.27048 19.8041 3.83198C20.4167 4.39348 20.906 5.07613 21.241 5.83665C21.5759 6.59717 21.7493 7.41898 21.75 8.25V8.25Z" />
    </IconStrokeBase>
  );
};

export const IconLock = (props: IconProps) => {
  return (
    <IconStrokeBase title="Restricted" {...props}>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </IconStrokeBase>
  );
};

export const IconGithub = (props: IconProps) => {
  return (
    <IconStrokeBase title="GitHub" {...props}>
      <path d="M9 19C4 20.5 4 16.5 2 16M16 22V18.13C16.0375 17.6532 15.9731 17.1738 15.811 16.7238C15.6489 16.2738 15.3929 15.8634 15.06 15.52C18.2 15.17 21.5 13.98 21.5 8.52C21.4997 7.12383 20.9627 5.7812 20 4.77C20.4559 3.54851 20.4236 2.19835 19.91 1C19.91 1 18.73 0.650001 16 2.48C13.708 1.85882 11.292 1.85882 9 2.48C6.27 0.650001 5.09 1 5.09 1C4.57638 2.19835 4.54414 3.54851 5 4.77C4.03013 5.7887 3.49252 7.14346 3.5 8.55C3.5 13.97 6.8 15.16 9.94 15.55C9.611 15.89 9.35726 16.2954 9.19531 16.7399C9.03335 17.1844 8.96681 17.6581 9 18.13V22" />
    </IconStrokeBase>
  );
};

export const IconSource = (props: IconProps) => {
  return (
    <IconStrokeBase title="Source" {...props}>
      <path d="M16.24 7.75999C16.7979 8.31723 17.2404 8.97896 17.5424 9.70735C17.8443 10.4357 17.9997 11.2165 17.9997 12.005C17.9997 12.7935 17.8443 13.5742 17.5424 14.3026C17.2404 15.031 16.7979 15.6928 16.24 16.25M7.76 16.24C7.20214 15.6828 6.75959 15.021 6.45764 14.2926C6.1557 13.5642 6.00028 12.7835 6.00028 11.995C6.00028 11.2065 6.1557 10.4257 6.45764 9.69735C6.75959 8.96896 7.20214 8.30723 7.76 7.74999M19.07 4.92999C20.9447 6.80527 21.9979 9.34835 21.9979 12C21.9979 14.6516 20.9447 17.1947 19.07 19.07M4.93 19.07C3.05529 17.1947 2.00214 14.6516 2.00214 12C2.00214 9.34835 3.05529 6.80527 4.93 4.92999M14 12C14 13.1046 13.1046 14 12 14C10.8954 14 10 13.1046 10 12C10 10.8954 10.8954 10 12 10C13.1046 10 14 10.8954 14 12Z" />
    </IconStrokeBase>
  );
};

export const IconDiff = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Diff" {...props}>
      <path d="M3 3.25L3 20.75C3 21.44 3.6048 22 4.35 22L19.65 22C20.3952 22 21 21.44 21 20.75L21 3.25C21 2.56 20.3952 2 19.65 2L4.35 2C3.6048 2 3 2.56 3 3.25Z" />
      <path d="M15.25 9.25L8.75 9.25M12 12.5L12 6" />
      <path d="M8.75 17L15.25 17" />
    </IconStrokeBase>
  );
};

export const IconMap = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Map" {...props}>
      <path d="M1 6V22L8 18L16 22L23 18V2L16 6L8 2L1 6Z" />
      <path d="M8 2V18" />
      <path d="M16 6V22" />
    </IconStrokeBase>
  );
};

export const IconHome = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Home" {...props}>
      <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" />
      <path d="M9 22V12H15V22" />
    </IconStrokeBase>
  );
};

export const IconFit = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Zoom to Fit" {...props}>
      <path d="M8 3H5C4.46957 3 3.96086 3.21071 3.58579 3.58579C3.21071 3.96086 3 4.46957 3 5V8M21 8V5C21 4.46957 20.7893 3.96086 20.4142 3.58579C20.0391 3.21071 19.5304 3 19 3H16M16 21H19C19.5304 21 20.0391 20.7893 20.4142 20.4142C20.7893 20.0391 21 19.5304 21 19V16M3 16V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H8" />
    </IconStrokeBase>
  );
};

export const IconEyeOpen = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Show" {...props}>
      <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" />
      <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" />
    </IconStrokeBase>
  );
};

export const IconEyeClosed = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Hide" {...props}>
      <path d="M17.94 17.94C16.2306 19.243 14.1491 19.9649 12 20C5 20 1 12 1 12C2.24389 9.68192 3.96914 7.65663 6.06 6.06003M9.9 4.24002C10.5883 4.0789 11.2931 3.99836 12 4.00003C19 4.00003 23 12 23 12C22.393 13.1356 21.6691 14.2048 20.84 15.19M14.12 14.12C13.8454 14.4148 13.5141 14.6512 13.1462 14.8151C12.7782 14.9791 12.3809 15.0673 11.9781 15.0744C11.5753 15.0815 11.1752 15.0074 10.8016 14.8565C10.4281 14.7056 10.0887 14.4811 9.80385 14.1962C9.51897 13.9113 9.29439 13.572 9.14351 13.1984C8.99262 12.8249 8.91853 12.4247 8.92563 12.0219C8.93274 11.6191 9.02091 11.2219 9.18488 10.8539C9.34884 10.4859 9.58525 10.1547 9.88 9.88003" />
      <path d="M3 3L21 21" />
    </IconStrokeBase>
  );
};

export const IconTarget = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Center" {...props}>
      <path d="M21.25 12C21.25 17.1086 17.1086 21.25 12 21.25C6.89137 21.25 2.75 17.1086 2.75 12C2.75 6.89137 6.89137 2.75 12 2.75C17.1086 2.75 21.25 6.89137 21.25 12Z" />
      <path d="M21 12H18" />
      <path d="M6 12H3" />
      <path d="M12 6V3" />
      <path d="M12 21V18" />
      <path
        d="M14.25 12C14.25 13.2426 13.2426 14.25 12 14.25C10.7574 14.25 9.75 13.2426 9.75 12C9.75 10.7574 10.7574 9.75 12 9.75C13.2426 9.75 14.25 10.7574 14.25 12Z"
        fill="#111920"
      />
    </IconStrokeBase>
  );
};

export const IconCommit = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Commit" {...props}>
      <path d="M12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16Z" />
      <path d="M1.05 12H7" />
      <path d="M17.01 12H22.96" />
    </IconStrokeBase>
  );
};

export const IconExternalResource = (props: IconProps) => {
  return (
    <IconStrokeBase title="External Resource" {...props}>
      <path d="M4 7L20 7L20 3L4 3L4 7Z" />
      <path d="M8 14L16 14L16 10L8 10L8 14Z" />
      <path d="M10 21L14 21L14 17L10 17L10 21Z" />
      <path d="M12 10L12 7" />
      <path d="M12 17L12 14" />
    </IconStrokeBase>
  );
};

export const IconScaleDown = (props: IconProps) => {
  return (
    <IconStrokeBase
      color="#4361FF"
      strokeWidth="1.5"
      title="Over-provisioned"
      viewBox="0 0 16 16"
      {...props}
    >
      <path d="M12.6663 9.33333L7.99967 14M7.99967 14L3.33301 9.33333M7.99967 14V2" />
    </IconStrokeBase>
  );
};

export const IconScaleUp = (props: IconProps) => {
  return (
    <IconStrokeBase
      color="#AD1A1A"
      strokeWidth="1.5"
      title="Under-provisioned"
      viewBox="0 0 16 16"
      {...props}
    >
      <path d="M3.33301 6.66667L7.99967 2M7.99967 2L12.6663 6.66667M7.99967 2V14" />
    </IconStrokeBase>
  );
};

export const IconScaleCheck = (props: IconProps) => {
  return (
    <IconStrokeBase
      strokeWidth="1.5"
      title="Scale Check"
      viewBox="0 0 16 16"
      {...props}
    >
      <path d="M3 8.5L7 12.5L13 3.5" />
    </IconStrokeBase>
  );
};

export const IconClock = (props: IconProps) => {
  return (
    <IconStrokeBase
      color="#825804"
      strokeWidth="1.5"
      title="Queued"
      viewBox="0 0 16 16"
      {...props}
    >
      <circle cx="8" cy="8" r="6" />
      <path d="M8 5V8L10.5 9.5" />
    </IconStrokeBase>
  );
};

export const IconDiagnostics = (props: IconProps) => {
  return (
    <IconStrokeBase title="Diagnostics" viewBox="0 0 24 24" {...props}>
      <path d="M16.5 7.5V16.5M12 10.875V16.5M7.5 14.25V16.5M5.25 21H18.75C19.3467 21 19.919 20.7629 20.341 20.341C20.7629 19.919 21 19.3467 21 18.75V5.25C21 4.65326 20.7629 4.08097 20.341 3.65901C19.919 3.23705 19.3467 3 18.75 3H5.25C4.65326 3 4.08097 3.23705 3.65901 3.65901C3.23705 4.08097 3 4.65326 3 5.25V18.75C3 19.3467 3.23705 19.919 3.65901 20.341C4.08097 20.7629 4.65326 21 5.25 21Z" />
    </IconStrokeBase>
  );
};

export const IconAI = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="0.5" fill="#111920" title="AI" {...props}>
      <path d="M9.813 15.904L9 18.75L8.187 15.904C7.97687 15.1689 7.5829 14.4994 7.04226 13.9587C6.50162 13.4181 5.83214 13.0241 5.097 12.814L2.25 12L5.096 11.187C5.83114 10.9769 6.50062 10.5829 7.04126 10.0423C7.5819 9.50162 7.97587 8.83214 8.186 8.097L9 5.25L9.813 8.096C10.0231 8.83114 10.4171 9.50062 10.9577 10.0413C11.4984 10.5819 12.1679 10.9759 12.903 11.186L15.75 12L12.904 12.813C12.1689 13.0231 11.4994 13.4171 10.9587 13.9577C10.4181 14.4984 10.0241 15.1679 9.814 15.903L9.813 15.904Z" />
      <path d="M18.259 8.715L18 9.75L17.741 8.715C17.5927 8.12159 17.286 7.57962 16.8536 7.14703C16.4212 6.71444 15.8794 6.40749 15.286 6.259L14.25 6L15.286 5.741C15.8794 5.59251 16.4212 5.28556 16.8536 4.85297C17.286 4.42038 17.5927 3.87841 17.741 3.285L18 2.25L18.259 3.285C18.4073 3.87854 18.7142 4.42059 19.1468 4.85319C19.5794 5.28579 20.1215 5.59267 20.715 5.741L21.75 6L20.715 6.259C20.1215 6.40733 19.5794 6.71421 19.1468 7.14681C18.7142 7.57941 18.4073 8.12147 18.259 8.715Z" />
      <path d="M16.894 20.567L16.5 21.75L16.106 20.567C15.9955 20.2356 15.8094 19.9345 15.5625 19.6875C15.3155 19.4406 15.0144 19.2545 14.683 19.144L13.5 18.75L14.683 18.356C15.0144 18.2455 15.3155 18.0594 15.5625 17.8125C15.8094 17.5655 15.9955 17.2644 16.106 16.933L16.5 15.75L16.894 16.933C17.0045 17.2644 17.1906 17.5655 17.4375 17.8125C17.6845 18.0594 17.9856 18.2455 18.317 18.356L19.5 18.75L18.317 19.144C17.9856 19.2545 17.6845 19.4406 17.4375 19.6875C17.1906 19.9345 17.0045 20.2356 16.894 20.567Z" />
    </IconStrokeBase>
  );
};

export const IconCalendar = (props: IconProps) => {
  return (
    <IconStrokeBase title="Calendar" {...props}>
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </IconStrokeBase>
  );
};

export const IconAws = (props: IconProps) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      aria-label="AWS Icon"
      role="img"
      {...props}
    >
      <rect width="32" height="32" rx="4" fill="#F3F4F6" />
      <path
        d="M10.4406 13.7011C10.4406 14.0242 10.4757 14.2861 10.5372 14.4782C10.6075 14.6702 10.6953 14.8798 10.8182 15.1068C10.8621 15.1766 10.8797 15.2465 10.8797 15.3076C10.8797 15.3949 10.827 15.4822 10.7128 15.5695L10.1596 15.9362C10.0806 15.9886 10.0016 16.0148 9.93131 16.0148C9.8435 16.0148 9.75568 15.9711 9.66787 15.8925C9.54494 15.7616 9.43956 15.6219 9.35175 15.4822C9.26394 15.3338 9.17612 15.1679 9.07953 14.9671C8.3946 15.7703 7.53404 16.1719 6.49785 16.1719C5.76023 16.1719 5.17189 15.9624 4.74161 15.5433C4.31133 15.1242 4.0918 14.5655 4.0918 13.867C4.0918 13.1249 4.35523 12.5225 4.89089 12.0685C5.42654 11.6145 6.13782 11.3875 7.04229 11.3875C7.34085 11.3875 7.64819 11.4137 7.9731 11.4573C8.298 11.501 8.63169 11.5708 8.98294 11.6494V11.0121C8.98294 10.3486 8.84244 9.88583 8.57022 9.61518C8.28922 9.34452 7.81503 9.21356 7.13888 9.21356C6.83154 9.21356 6.51541 9.24849 6.19051 9.32706C5.8656 9.40564 5.54948 9.50168 5.24214 9.62391C5.10164 9.68502 4.99626 9.71994 4.93479 9.7374C4.87333 9.75487 4.82942 9.7636 4.79429 9.7636C4.67136 9.7636 4.60989 9.67629 4.60989 9.49295V9.06514C4.60989 8.92545 4.62745 8.82068 4.67136 8.75957C4.71526 8.69845 4.79429 8.63734 4.91723 8.57622C5.22457 8.41907 5.59339 8.28811 6.02367 8.18334C6.45395 8.06985 6.91057 8.01746 7.39354 8.01746C8.4385 8.01746 9.20247 8.25319 9.69422 8.72465C10.1772 9.1961 10.4231 9.91202 10.4231 10.8724V13.7011H10.4406ZM6.87544 15.0282C7.16522 15.0282 7.46379 14.9758 7.77991 14.871C8.09603 14.7663 8.37703 14.5742 8.61413 14.3123C8.75463 14.1464 8.86 13.9631 8.91269 13.7535C8.96537 13.544 9.0005 13.2908 9.0005 12.9939V12.6273C8.74584 12.5661 8.47363 12.5138 8.19263 12.4788C7.91163 12.4439 7.63941 12.4265 7.36719 12.4265C6.77885 12.4265 6.34857 12.54 6.05879 12.7757C5.76901 13.0114 5.62851 13.3432 5.62851 13.7797C5.62851 14.19 5.73389 14.4956 5.95342 14.7052C6.16417 14.9234 6.47151 15.0282 6.87544 15.0282ZM13.9268 15.9711C13.7687 15.9711 13.6633 15.9449 13.5931 15.8838C13.5228 15.8314 13.4614 15.7092 13.4087 15.5433L11.3451 8.79449C11.2924 8.61988 11.2661 8.50638 11.2661 8.44526C11.2661 8.30557 11.3363 8.227 11.4768 8.227H12.3374C12.5042 8.227 12.6184 8.25319 12.6798 8.3143C12.7501 8.36669 12.8028 8.48892 12.8555 8.6548L14.3307 14.4345L15.7006 8.6548C15.7445 8.48019 15.7972 8.36669 15.8674 8.3143C15.9377 8.26192 16.0606 8.227 16.2187 8.227H16.9212C17.088 8.227 17.2022 8.25319 17.2724 8.3143C17.3427 8.36669 17.4041 8.48892 17.4393 8.6548L18.8267 14.5044L20.3458 8.6548C20.3985 8.48019 20.46 8.36669 20.5215 8.3143C20.5917 8.26192 20.7059 8.227 20.8639 8.227H21.6806C21.8211 8.227 21.9001 8.29684 21.9001 8.44526C21.9001 8.48892 21.8913 8.53257 21.8826 8.58496C21.8738 8.63734 21.8562 8.70718 21.8211 8.80322L19.7048 15.552C19.6521 15.7266 19.5907 15.8401 19.5204 15.8925C19.4502 15.9449 19.336 15.9798 19.1867 15.9798H18.4315C18.2647 15.9798 18.1505 15.9536 18.0803 15.8925C18.01 15.8314 17.9486 15.7179 17.9134 15.5433L16.5524 9.91202L15.2 15.5346C15.1561 15.7092 15.1034 15.8227 15.0332 15.8838C14.9629 15.9449 14.84 15.9711 14.6819 15.9711H13.9268ZM25.2106 16.2068C24.754 16.2068 24.2974 16.1545 23.8583 16.0497C23.4193 15.9449 23.0768 15.8314 22.8485 15.7005C22.708 15.6219 22.6114 15.5346 22.5763 15.456C22.5411 15.3774 22.5236 15.2901 22.5236 15.2115V14.7663C22.5236 14.5829 22.5938 14.4956 22.7255 14.4956C22.7782 14.4956 22.8309 14.5044 22.8836 14.5218C22.9363 14.5393 23.0153 14.5742 23.1031 14.6091C23.4017 14.7401 23.7266 14.8449 24.0691 14.9147C24.4203 14.9845 24.7628 15.0195 25.114 15.0195C25.6673 15.0195 26.0975 14.9234 26.3961 14.7314C26.6947 14.5393 26.8527 14.2599 26.8527 13.9019C26.8527 13.6575 26.7737 13.4567 26.6156 13.2908C26.4576 13.1249 26.159 12.9765 25.7287 12.8368L24.4554 12.4439C23.8144 12.2431 23.3402 11.9463 23.0505 11.5534C22.7607 11.1692 22.6114 10.7414 22.6114 10.2874C22.6114 9.92075 22.6904 9.59771 22.8485 9.31833C23.0065 9.03895 23.2173 8.79449 23.4807 8.60242C23.7442 8.40161 24.0427 8.25319 24.394 8.14842C24.7452 8.04365 25.114 8 25.5004 8C25.6936 8 25.8956 8.00873 26.0888 8.03492C26.2907 8.06111 26.4751 8.09604 26.6595 8.13096C26.8352 8.17461 27.002 8.21827 27.1601 8.27065C27.3181 8.32304 27.4411 8.37542 27.5289 8.4278C27.6518 8.49765 27.7396 8.56749 27.7923 8.64607C27.845 8.71592 27.8713 8.81195 27.8713 8.93418V9.34452C27.8713 9.52787 27.8011 9.62391 27.6694 9.62391C27.5991 9.62391 27.485 9.58898 27.3357 9.51914C26.8352 9.29214 26.2732 9.17864 25.6497 9.17864C25.1492 9.17864 24.754 9.25722 24.4818 9.4231C24.2096 9.58898 24.0691 9.84217 24.0691 10.2001C24.0691 10.4446 24.1569 10.6541 24.3325 10.82C24.5081 10.9859 24.833 11.1518 25.2984 11.3002L26.5454 11.6931C27.1776 11.8939 27.6343 12.1733 27.9065 12.5312C28.1787 12.8892 28.3104 13.2995 28.3104 13.7535C28.3104 14.1289 28.2314 14.4694 28.0821 14.7663C27.924 15.0631 27.7133 15.325 27.4411 15.5346C27.1688 15.7528 26.8439 15.91 26.4663 16.0235C26.0712 16.1457 25.6585 16.2068 25.2106 16.2068Z"
        fill="#252F3E"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M26.8702 20.4501C23.9812 22.5717 19.7837 23.698 16.1746 23.698C11.1167 23.698 6.55921 21.8383 3.11697 18.7477C2.84475 18.5032 3.09062 18.1714 3.41553 18.3635C7.13877 20.5113 11.7313 21.8121 16.482 21.8121C19.6871 21.8121 23.2084 21.1486 26.4487 19.7866C26.9316 19.5683 27.3444 20.1009 26.8702 20.4501Z"
        fill="#FF9900"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M28.0731 19.0882C27.7043 18.6167 25.632 18.8612 24.6924 18.9747C24.4114 19.0096 24.3675 18.7651 24.6221 18.5818C26.273 17.4293 28.9864 17.7611 29.3025 18.1453C29.6186 18.5381 29.2147 21.2359 27.6692 22.5281C27.4321 22.7289 27.2038 22.6241 27.3092 22.3622C27.6604 21.4978 28.442 19.5509 28.0731 19.0882Z"
        fill="#FF9900"
      />
    </svg>
  );
};

export const IconCube = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Cube" {...props}>
      <path d="M22.1538 6.92308L12 1L1.84615 6.92308M22.1538 6.92308L12 12.8462M22.1538 6.92308V17.0769L12 23M1.84615 6.92308L12 12.8462M1.84615 6.92308V17.0769L12 23M12 12.8462V23" />
    </IconStrokeBase>
  );
};

export const IconUsers = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Users" {...props}>
      <path d="M16.6154 20.3072V18.4611C16.6154 17.4818 16.2264 16.5427 15.5339 15.8502C14.8415 15.1578 13.9023 14.7688 12.9231 14.7688H5.53844C4.55918 14.7688 3.62002 15.1578 2.92758 15.8502C2.23514 16.5427 1.84613 17.4818 1.84613 18.4611V20.3072M22.1538 20.3072V18.4611C22.1532 17.643 21.8809 16.8482 21.3797 16.2017C20.8785 15.5551 20.1767 15.0933 19.3846 14.8888M15.6923 3.81184C16.4865 4.01519 17.1905 4.4771 17.6932 5.12474C18.1959 5.77238 18.4688 6.56891 18.4688 7.38876C18.4688 8.20861 18.1959 9.00514 17.6932 9.65278C17.1905 10.3004 16.4865 10.7623 15.6923 10.9657M12.9231 7.38414C12.9231 9.42335 11.27 11.0764 9.23075 11.0764C7.19154 11.0764 5.53844 9.42335 5.53844 7.38414C5.53844 5.34494 7.19154 3.69183 9.23075 3.69183C11.27 3.69183 12.9231 5.34494 12.9231 7.38414Z" />
    </IconStrokeBase>
  );
};

export const IconArrowUp = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Arrow Up" {...props}>
      <path d="M5 10L12 3M12 3L19 10M12 3V21" />
    </IconStrokeBase>
  );
};

export const IconArrowDown = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Arrow Down" {...props}>
      <path d="M19 14L12 21M12 21L5 14M12 21V3" />
    </IconStrokeBase>
  );
};

export const IconDuplicate = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Duplicate" {...props}>
      <path d="M8 16H6C5.46957 16 4.96086 15.7893 4.58579 15.4142C4.21071 15.0391 4 14.5304 4 14V6C4 5.46957 4.21071 4.96086 4.58579 4.58579C4.96086 4.21071 5.46957 4 6 4H14C14.5304 4 15.0391 4.21071 15.4142 4.58579C15.7893 4.96086 16 5.46957 16 6V8M10 20H18C18.5304 20 19.0391 19.7893 19.4142 19.4142C19.7893 19.0391 20 18.5304 20 18V10C20 9.46957 19.7893 8.96086 19.4142 8.58579C19.0391 8.21071 18.5304 8 18 8H10C9.46957 8 8.96086 8.21071 8.58579 8.58579C8.21071 8.96086 8 9.46957 8 10V18C8 18.5304 8.21071 19.0391 8.58579 19.4142C8.96086 19.7893 9.46957 20 10 20Z" />
    </IconStrokeBase>
  );
};

export const IconCard = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Card" {...props}>
      <path d="M2.25 8.25H21.75M2.25 9H21.75M6.25 15.25H11.25M4.5 19.5H19.5C20.0967 19.5 20.669 19.2629 21.091 18.841C21.5129 18.419 21.75 17.8467 21.75 17.25V6.75C21.75 6.15326 21.5129 5.58097 21.091 5.15901C20.669 4.73705 20.0967 4.5 19.5 4.5H4.5C3.90326 4.5 3.33097 4.73705 2.90901 5.15901C2.48705 5.58097 2.25 6.15326 2.25 6.75V17.25C2.25 17.8467 2.48705 18.419 2.90901 18.841C3.33097 19.2629 3.90326 19.5 4.5 19.5Z" />
    </IconStrokeBase>
  );
};

export const IconCircleStack = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Circle Stack" {...props}>
      <path d="M20.8846 5.94231C20.8846 8.39554 16.9065 10.3846 12 10.3846C7.09354 10.3846 3.11538 8.39554 3.11538 5.94231M20.8846 5.94231C20.8846 3.48908 16.9065 1.5 12 1.5C7.09354 1.5 3.11538 3.48908 3.11538 5.94231M20.8846 5.94231V18.0577C20.8846 20.5109 16.9065 22.5 12 22.5C7.09354 22.5 3.11538 20.5109 3.11538 18.0577V5.94231M20.8846 5.94231V9.98077M3.11538 5.94231V9.98077M20.8846 9.98077V14.0192C20.8846 16.4725 16.9065 18.4615 12 18.4615C7.09354 18.4615 3.11538 16.4725 3.11538 14.0192V9.98077M20.8846 9.98077C20.8846 12.434 16.9065 14.4231 12 14.4231C7.09354 14.4231 3.11538 12.434 3.11538 9.98077" />
    </IconStrokeBase>
  );
};

export const IconChartSquareBar = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Chart Square Bar" {...props}>
      <path d="M16.5 7.5V16.5M12 10.875V16.5M7.5 14.25V16.5M5.25 21H18.75C19.3467 21 19.919 20.7629 20.341 20.341C20.7629 19.919 21 19.3467 21 18.75V5.25C21 4.65326 20.7629 4.08097 20.341 3.65901C19.919 3.23705 19.3467 3 18.75 3H5.25C4.65326 3 4.08097 3.23705 3.65901 3.65901C3.23705 4.08097 3 4.65326 3 5.25V18.75C3 19.3467 3.23705 19.919 3.65901 20.341C4.08097 20.7629 4.65326 21 5.25 21Z" />
    </IconStrokeBase>
  );
};

export const IconPencil = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Pencil" {...props}>
      <path d="M15.232 5.232L18.768 8.768M16.732 3.732C17.2009 3.2631 17.8369 2.99967 18.5 2.99967C19.1631 2.99967 19.7991 3.2631 20.268 3.732C20.7369 4.2009 21.0003 4.83687 21.0003 5.5C21.0003 6.16313 20.7369 6.7991 20.268 7.268L6.5 21.036H3V17.464L16.732 3.732Z" />
    </IconStrokeBase>
  );
};

export const IconEllipsisHorizontal = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Ellipsis Horizontal" {...props}>
      <path d="M6.75 12C6.75 12.1989 6.67098 12.3897 6.53033 12.5303C6.38968 12.671 6.19891 12.75 6 12.75C5.80109 12.75 5.61032 12.671 5.46967 12.5303C5.32902 12.3897 5.25 12.1989 5.25 12C5.25 11.8011 5.32902 11.6103 5.46967 11.4697C5.61032 11.329 5.80109 11.25 6 11.25C6.19891 11.25 6.38968 11.329 6.53033 11.4697C6.67098 11.6103 6.75 11.8011 6.75 12Z" />
      <path d="M12.75 12C12.75 12.1989 12.671 12.3897 12.5303 12.5303C12.3897 12.671 12.1989 12.75 12 12.75C11.8011 12.75 11.6103 12.671 11.4697 12.5303C11.329 12.3897 11.25 12.1989 11.25 12C11.25 11.8011 11.329 11.6103 11.4697 11.4697C11.6103 11.329 11.8011 11.25 12 11.25C12.1989 11.25 12.3897 11.329 12.5303 11.4697C12.671 11.6103 12.75 11.8011 12.75 12Z" />
      <path d="M18.75 12C18.75 12.1989 18.671 12.3897 18.5303 12.5303C18.3897 12.671 18.1989 12.75 18 12.75C17.8011 12.75 17.6103 12.671 17.4697 12.5303C17.329 12.3897 17.25 12.1989 17.25 12C17.25 11.8011 17.329 11.6103 17.4697 11.4697C17.6103 11.329 17.8011 11.25 18 11.25C18.1989 11.25 18.3897 11.329 18.5303 11.4697C18.671 11.6103 18.75 11.8011 18.75 12Z" />
    </IconStrokeBase>
  );
};

export const IconGlobeAlt = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Globe" {...props}>
      <path d="M12 22C14.2167 21.9999 16.3705 21.2636 18.1234 19.9067C19.8762 18.5498 21.1288 16.6492 21.6844 14.5033M12 22C9.78334 21.9999 7.62949 21.2636 5.87664 19.9067C4.12379 18.5498 2.8712 16.6492 2.31556 14.5033M12 22C14.7611 22 17 17.5222 17 12C17 6.47778 14.7611 2 12 2M12 22C9.23889 22 7 17.5222 7 12C7 6.47778 9.23889 2 12 2M12 2C13.7736 1.99926 15.5155 2.4704 17.0469 3.36507C18.5783 4.25975 19.8441 5.54574 20.7145 7.09111M12 2C10.2264 1.99926 8.48454 2.4704 6.95312 3.36507C5.42171 4.25975 4.1559 5.54574 3.28556 7.09111M20.7145 7.09111C18.2952 9.18659 15.2006 10.3379 12 10.3333C8.66889 10.3333 5.62223 9.11111 3.28556 7.09111M20.7145 7.09111C21.5598 8.58888 22.0027 10.2801 22 12C22 12.8644 21.89 13.7033 21.6844 14.5033M21.6844 14.5033C18.7213 16.146 15.388 17.0053 12 17C8.48667 17 5.18556 16.0944 2.31556 14.5033M2.31556 14.5033C2.10528 13.6855 1.99925 12.8444 2 12C2 10.2167 2.46667 8.54111 3.28556 7.09111" />
    </IconStrokeBase>
  );
};

export const IconBars3 = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Bars 3" {...props}>
      <path d="M3.75 6.75H20.25M3.75 12H20.25M3.75 17.25H20.25" />
    </IconStrokeBase>
  );
};

export const IconStack = (props: IconProps) => {
  return (
    <IconStrokeBase
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="square"
      title="Stack"
      {...props}
    >
      <path d="M2.90909 7.45455L12 2L21.0909 7.45455L12 12.9091L2.90909 7.45455Z" />
      <path d="M21.0909 12L12 17.4545L2.90909 12" />
      <path d="M21.0909 16.5455L12 22L2.90909 16.5455" />
    </IconStrokeBase>
  );
};

export const IconUnlock = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Unlock" {...props}>
      <path d="M19 11H5C3.89543 11 3 11.8954 3 13V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V13C21 11.8954 20.1046 11 19 11Z" />
      <path d="M7 11V6.99997C6.99876 5.76002 7.45828 4.56384 8.28938 3.64364C9.12047 2.72344 10.2638 2.14487 11.4975 2.02026C12.7312 1.89565 13.9671 2.23387 14.9655 2.96928C15.9638 3.70469 16.6533 4.78482 16.9 5.99997" />
    </IconStrokeBase>
  );
};

export const IconMagnifyingGlass = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Magnifying Glass" {...props}>
      <path d="M21 21L15.803 15.803M15.803 15.803C17.2096 14.3964 17.9998 12.4887 17.9998 10.4995C17.9998 8.51029 17.2096 6.60256 15.803 5.19599C14.3964 3.78941 12.4887 2.99921 10.4995 2.99921C8.51031 2.99921 6.60258 3.78941 5.196 5.19599C3.78943 6.60256 2.99922 8.51029 2.99922 10.4995C2.99922 12.4887 3.78943 14.3964 5.196 15.803C6.60258 17.2096 8.51031 17.9998 10.4995 17.9998C12.4887 17.9998 14.3964 17.2096 15.803 15.803V15.803Z" />
    </IconStrokeBase>
  );
};

export const IconShieldCheck = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Shield with Check" {...props}>
      <path d="M8.66667 12.2455L10.8889 14.5082L15.3333 9.9828M21.5756 5.4393C18.0623 5.62927 14.6186 4.39237 12 2C9.38141 4.39237 5.93767 5.62927 2.42445 5.4393C2.14171 6.55381 1.99909 7.70036 2 8.85145C2 15.1768 6.24889 20.493 12 22C17.7511 20.493 22 15.178 22 8.85145C22 7.67259 21.8522 6.52992 21.5756 5.4393Z" />
    </IconStrokeBase>
  );
};

export const IconThumbsDown = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Thumbs Down" {...props}>
      <path d="M16.6826 13.0001L12.6826 22.0001C11.887 22.0001 11.1239 21.6841 10.5613 21.1214C9.99872 20.5588 9.68265 19.7958 9.68265 19.0001V15.0001H4.02265C3.73274 15.0034 3.44558 14.9436 3.18108 14.8249C2.91657 14.7062 2.68103 14.5314 2.49079 14.3126C2.30055 14.0938 2.16014 13.8363 2.07931 13.5579C1.99847 13.2794 1.97914 12.9868 2.02265 12.7001L3.40265 3.70012C3.47497 3.22322 3.71721 2.78852 4.08474 2.47612C4.45227 2.16372 4.92032 1.99467 5.40265 2.00012H16.6826M16.6826 13.0001V2.00012M16.6826 13.0001H19.6826C20.2131 13.0001 20.7218 12.7894 21.0969 12.4143C21.4719 12.0393 21.6826 11.5306 21.6826 11.0001V4.00012C21.6826 3.46969 21.4719 2.96098 21.0969 2.58591C20.7218 2.21084 20.2131 2.00012 19.6826 2.00012H16.6826" />
    </IconStrokeBase>
  );
};

export const IconUser = (props: IconProps) => {
  return (
    <IconStrokeBase title="User" {...props}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </IconStrokeBase>
  );
};

export const IconAptible = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="0" title="Aptible" {...props}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M23.9999 13.3365V8.80189L16.7999 2H12H11.9999H7.155L0 8.80189V13.3365L12 2.00004L23.9999 13.3365ZM16.8001 21.272H13.2001V10.8814V6.34676L16.8001 9.74771L24.0001 16.5496V21.0842L16.8001 14.2823V21.272ZM7.19997 21.272H10.8V10.8814L10.8 10.8814V6.34676L10.8 6.34679V6.34676L7.19997 9.74771V9.74773L0 16.5496V21.0842L7.19997 14.2823V21.272Z"
        fill="#111920"
      />
    </IconStrokeBase>
  );
};

export const IconBell = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Bell" {...props}>
      <path d="M14.857 17.082C16.7202 16.8614 18.5509 16.4217 20.311 15.772C18.8204 14.1208 17.9967 11.9745 18 9.75V9.05V9C18 7.4087 17.3679 5.88258 16.2426 4.75736C15.1174 3.63214 13.5913 3 12 3C10.4087 3 8.88258 3.63214 7.75736 4.75736C6.63215 5.88258 6 7.4087 6 9V9.75C6.00302 11.9746 5.17899 14.121 3.688 15.772C5.421 16.412 7.248 16.857 9.143 17.082M14.857 17.082C12.959 17.3071 11.041 17.3071 9.143 17.082M14.857 17.082C15.0011 17.5319 15.0369 18.0094 14.9616 18.4757C14.8862 18.942 14.7018 19.384 14.4234 19.7656C14.1449 20.1472 13.7803 20.4576 13.3592 20.6716C12.9381 20.8856 12.4724 20.9972 12 20.9972C11.5276 20.9972 11.0619 20.8856 10.6408 20.6716C10.2197 20.4576 9.85508 20.1472 9.57662 19.7656C9.29817 19.384 9.11377 18.942 9.03842 18.4757C8.96307 18.0094 8.9989 17.5319 9.143 17.082" />
    </IconStrokeBase>
  );
};

export const IconBitbucket = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Bitbucket" {...props}>
      <path d="M1.79259 2.00007C1.67825 1.99851 1.56496 2.02262 1.46071 2.07069C1.35646 2.11876 1.26378 2.18962 1.18921 2.27829C1.11463 2.36696 1.05997 2.47127 1.02908 2.58388C0.998189 2.69649 0.991815 2.81466 1.01041 2.93007L4.848 21.09C4.93356 21.6112 5.37353 21.995 5.88929 22H18.1109C18.2988 22.0019 18.4812 21.9351 18.6251 21.8115C18.769 21.688 18.8649 21.5158 18.8956 21.3262L22.9898 2.93882C23.0082 2.82345 23.0017 2.70536 22.9707 2.59285C22.9397 2.48033 22.885 2.37611 22.8105 2.2875C22.736 2.19888 22.6434 2.12802 22.5392 2.07988C22.4351 2.03173 22.3219 2.00748 22.2076 2.00882L1.79259 2.00007V2.00007Z" />
      <path d="M14.6667 16H9.33333L8 8H16L14.6667 16Z" />
    </IconStrokeBase>
  );
};

export const IconDeployments = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Deployments" {...props}>
      <path d="M12.006 16.6069V8.97256M12.006 8.97256L15.3908 12.3656M12.006 8.97256L8.62126 12.3656M6.08268 20C4.87481 20.0013 3.70603 19.5709 2.78625 18.7861C1.86647 18.0012 1.25594 16.9134 1.06431 15.7179C0.872685 14.5224 1.11251 13.2975 1.74072 12.2634C2.36892 11.2292 3.34435 10.4534 4.49184 10.0753C4.19703 8.56106 4.50172 6.99124 5.3414 5.69827C6.18108 4.40531 7.48981 3.49073 8.99044 3.1482C10.4911 2.80568 12.0658 3.06211 13.3811 3.86319C14.6964 4.66428 15.6491 5.9471 16.0373 7.44003C16.6375 7.24433 17.2804 7.22079 17.8933 7.37205C18.5062 7.52331 19.0647 7.84335 19.5057 8.29603C19.9467 8.7487 20.2527 9.31597 20.389 9.93377C20.5254 10.5516 20.4867 11.1953 20.2773 11.7922C21.2009 12.1459 21.972 12.8125 22.4566 13.6764C22.9412 14.5403 23.1089 15.5471 22.9305 16.522C22.7522 17.4969 22.239 18.3784 21.4801 19.0137C20.7211 19.649 19.7643 19.9979 18.7756 20H6.08268Z" />
    </IconStrokeBase>
  );
};

export const IconDocumentSearch = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Document Search" {...props}>
      <path d="M10 21H17C17.5304 21 18.0391 20.7893 18.4142 20.4142C18.7893 20.0391 19 19.5304 19 19V9.414C18.9999 9.1488 18.8946 8.89449 18.707 8.707L13.293 3.293C13.1055 3.10545 12.8512 3.00006 12.586 3H7C6.46957 3 5.96086 3.21071 5.58579 3.58579C5.21071 3.96086 5 4.46957 5 5V16M5 21L9.879 16.121M9.879 16.121C10.1567 16.4033 10.4876 16.6279 10.8525 16.7817C11.2174 16.9355 11.6092 17.0156 12.0052 17.0173C12.4012 17.0189 12.7936 16.9421 13.1599 16.7914C13.5261 16.6406 13.8588 16.4188 14.1388 16.1388C14.4189 15.8588 14.6408 15.5262 14.7916 15.16C14.9425 14.7938 15.0193 14.4014 15.0177 14.0054C15.0162 13.6094 14.9362 13.2176 14.7825 12.8526C14.6287 12.4877 14.4043 12.1568 14.122 11.879C13.5579 11.3239 12.7973 11.0142 12.0059 11.0173C11.2145 11.0204 10.4564 11.3362 9.8967 11.8957C9.33702 12.4553 9.02111 13.2133 9.0178 14.0047C9.01448 14.7961 9.32402 15.5568 9.879 16.121Z" />
    </IconStrokeBase>
  );
};

export const IconEnvelope = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Envelope" {...props}>
      <path d="M21.75 6.75V17.25C21.75 17.8467 21.5129 18.419 21.091 18.841C20.669 19.2629 20.0967 19.5 19.5 19.5H4.5C3.90326 19.5 3.33097 19.2629 2.90901 18.841C2.48705 18.419 2.25 17.8467 2.25 17.25V6.75M21.75 6.75C21.75 6.15326 21.5129 5.58097 21.091 5.15901C20.669 4.73705 20.0967 4.5 19.5 4.5H4.5C3.90326 4.5 3.33097 4.73705 2.90901 5.15901C2.48705 5.58097 2.25 6.15326 2.25 6.75M21.75 6.75V6.993C21.75 7.37715 21.6517 7.75491 21.4644 8.0903C21.2771 8.42569 21.0071 8.70754 20.68 8.909L13.18 13.524C12.8252 13.7425 12.4167 13.8582 12 13.8582C11.5833 13.8582 11.1748 13.7425 10.82 13.524L3.32 8.91C2.99292 8.70854 2.72287 8.42669 2.53557 8.0913C2.34827 7.75591 2.24996 7.37815 2.25 6.994V6.75" />
    </IconStrokeBase>
  );
};

export const IconFolder = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Folder" {...props}>
      <path d="M22 19C22 19.5304 21.7893 20.0391 21.4142 20.4142C21.0391 20.7893 20.5304 21 20 21H4C3.46957 21 2.96086 20.7893 2.58579 20.4142C2.21071 20.0391 2 19.5304 2 19V5C2 4.46957 2.21071 3.96086 2.58579 3.58579C2.96086 3.21071 3.46957 3 4 3H9L11 6H20C20.5304 6 21.0391 6.21071 21.4142 6.58579C21.7893 6.96086 22 7.46957 22 8V19Z" />
    </IconStrokeBase>
  );
};

export const IconGitCommit = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Git Commit" {...props}>
      <path d="M12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16Z" />
      <path d="M1.05 12H7" />
      <path d="M17.01 12H22.96" />
    </IconStrokeBase>
  );
};

export const IconGitlab = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Gitlab" {...props}>
      <path d="M22.65 14.39L12 22.13L1.35001 14.39C1.20723 14.285 1.10133 14.1375 1.04744 13.9687C0.993557 13.7999 0.994455 13.6183 1.05001 13.45L2.27001 9.67L4.71001 2.16C4.73367 2.09881 4.77135 2.04401 4.82001 2C4.89925 1.92762 5.00269 1.88749 5.11001 1.88749C5.21733 1.88749 5.32077 1.92762 5.40001 2C5.45139 2.04966 5.48925 2.11162 5.51001 2.18L7.95001 9.67H16.05L18.49 2.16C18.5137 2.09881 18.5513 2.04401 18.6 2C18.6792 1.92762 18.7827 1.88749 18.89 1.88749C18.9973 1.88749 19.1008 1.92762 19.18 2C19.2314 2.04966 19.2693 2.11162 19.29 2.18L21.73 9.69L23 13.45C23.0505 13.6235 23.0438 13.8086 22.9807 13.978C22.9177 14.1473 22.8017 14.2918 22.65 14.39V14.39Z" />
    </IconStrokeBase>
  );
};

export const IconSwitchHorizontal = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Switch Horizontal" {...props}>
      <path d="M4 17L8 13M8 7H20H8ZM20 7L16 3L20 7ZM20 7L16 11L20 7ZM16 17H4H16ZM4 17L8 21L4 17Z" />
    </IconStrokeBase>
  );
};

export const IconSwitchVertical = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Switch Vertical" {...props}>
      <path d="M17 20L13 16M7 16V4V16ZM7 4L3 8L7 4ZM7 4L11 8L7 4ZM17 8V20V8ZM17 20L21 16L17 20Z" />
    </IconStrokeBase>
  );
};

export const IconTool = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Tool" {...props}>
      <path d="M14.7 6.29998C14.5168 6.48691 14.4141 6.73823 14.4141 6.99998C14.4141 7.26173 14.5168 7.51305 14.7 7.69998L16.3 9.29998C16.4869 9.48321 16.7382 9.58584 17 9.58584C17.2618 9.58584 17.5131 9.48321 17.7 9.29998L21.47 5.52998C21.9728 6.64117 22.1251 7.87921 21.9065 9.07913C21.6878 10.279 21.1087 11.3838 20.2463 12.2463C19.3838 13.1087 18.2791 13.6878 17.0791 13.9064C15.8792 14.1251 14.6412 13.9728 13.53 13.47L6.62 20.38C6.22218 20.7778 5.68261 21.0013 5.12 21.0013C4.55739 21.0013 4.01783 20.7778 3.62 20.38C3.22218 19.9822 2.99868 19.4426 2.99868 18.88C2.99868 18.3174 3.22218 17.7778 3.62 17.38L10.53 10.47C10.0272 9.35879 9.87492 8.12075 10.0935 6.92083C10.3122 5.72092 10.8913 4.61614 11.7537 3.7537C12.6162 2.89127 13.7209 2.31215 14.9209 2.09352C16.1208 1.8749 17.3588 2.02714 18.47 2.52998L14.71 6.28998L14.7 6.29998Z" />
    </IconStrokeBase>
  );
};

export const IconFileText = (props: IconProps) => {
  return (
    <IconStrokeBase
      color="#595E63"
      strokeWidth="1.5"
      title="File Text"
      {...props}
    >
      <path d="M9.33268 1.33333H3.99935C3.64573 1.33333 3.30659 1.4738 3.05654 1.72385C2.80649 1.9739 2.66602 2.31304 2.66602 2.66666V13.3333C2.66602 13.6869 2.80649 14.0261 3.05654 14.2761C3.30659 14.5262 3.64573 14.6667 3.99935 14.6667H11.9993C12.353 14.6667 12.6921 14.5262 12.9422 14.2761C13.1922 14.0261 13.3327 13.6869 13.3327 13.3333V5.33333M9.33268 1.33333L13.3327 5.33333M9.33268 1.33333V5.33333H13.3327M10.666 8.66666H5.33268M10.666 11.3333H5.33268M6.66602 5.99999H5.33268" />
    </IconStrokeBase>
  );
};

export const IconTerminal = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Terminal" {...props}>
      <path d="M4 17L10 11L4 5" />
      <path d="M12 19H20" />
    </IconStrokeBase>
  );
};

export const IconOrganization = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Organization" {...props}>
      <path d="M3.75 21H20.25M4.5 3H19.5M5.25 3V21M18.75 3V21M9 6.75H10.5M9 9.75H10.5M9 12.75H10.5M13.5 6.75H15M13.5 9.75H15M13.5 12.75H15M9 21V17.625C9 17.004 9.504 16.5 10.125 16.5H13.875C14.496 16.5 15 17.004 15 17.625V21" />
    </IconStrokeBase>
  );
};

export const IconActivity = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth="1.5" title="Activity" {...props}>
      <path d="M22 12H18L15 21L9 3L6 12H2" />
    </IconStrokeBase>
  );
};

export const IconList = (props: IconProps) => {
  return (
    <IconStrokeBase title="List" {...props}>
      <path d="M8 6H21M8 12H21M8 18H21M3 6H3.01M3 12H3.01M3 18H3.01" />
    </IconStrokeBase>
  );
};

export const IconGrid = (props: IconProps) => {
  return (
    <IconStrokeBase title="Grid" {...props}>
      <path d="M10 3H3V10H10V3Z" />
      <path d="M21 3H14V10H21V3Z" />
      <path d="M21 14H14V21H21V14Z" />
      <path d="M10 14H3V21H10V14Z" />
    </IconStrokeBase>
  );
};

export const IconFilter = (props: IconProps) => {
  return (
    <IconStrokeBase title="Filter" {...props}>
      <path d="M22 3H2L10 12.46V19L14 21V12.46L22 3Z" />
    </IconStrokeBase>
  );
};

export const IconPipelines = (props: IconProps) => {
  return (
    <IconStrokeBase title="Filter" {...props}>
      <path d="M7.22255 3.33398V16.6673M12.7781 3.33398V16.6673M2.70866 16.6673H17.292C17.867 16.6673 18.3337 16.2193 18.3337 15.6673V4.33398C18.3337 3.78198 17.867 3.33398 17.292 3.33398H2.70866C2.13366 3.33398 1.66699 3.78198 1.66699 4.33398V15.6673C1.66699 16.2193 2.13366 16.6673 2.70866 16.6673Z" />
    </IconStrokeBase>
  );
};

export const IconReorder = (props: IconProps) => {
  return (
    <IconStrokeBase title="Reorder" strokeWidth="1.5" {...props}>
      <path d="M14.5 17.25C14.6989 17.25 14.8897 17.329 15.0303 17.4697C15.171 17.6103 15.25 17.8011 15.25 18C15.25 18.1989 15.171 18.3897 15.0303 18.5303C14.8897 18.671 14.6989 18.75 14.5 18.75C14.3011 18.75 14.1103 18.671 13.9697 18.5303C13.829 18.3897 13.75 18.1989 13.75 18C13.75 17.8011 13.829 17.6103 13.9697 17.4697C14.1103 17.329 14.3011 17.25 14.5 17.25Z" />
      <path d="M14.5 11.25C14.6989 11.25 14.8897 11.329 15.0303 11.4697C15.171 11.6103 15.25 11.8011 15.25 12C15.25 12.1989 15.171 12.3897 15.0303 12.5303C14.8897 12.671 14.6989 12.75 14.5 12.75C14.3011 12.75 14.1103 12.671 13.9697 12.5303C13.829 12.3897 13.75 12.1989 13.75 12C13.75 11.8011 13.829 11.6103 13.9697 11.4697C14.1103 11.329 14.3011 11.25 14.5 11.25Z" />
      <path d="M14.5 5.25C14.6989 5.25 14.8897 5.32902 15.0303 5.46967C15.171 5.61032 15.25 5.80109 15.25 6C15.25 6.19891 15.171 6.38968 15.0303 6.53033C14.8897 6.67098 14.6989 6.75 14.5 6.75C14.3011 6.75 14.1103 6.67098 13.9697 6.53033C13.829 6.38968 13.75 6.19891 13.75 6C13.75 5.80109 13.829 5.61032 13.9697 5.46967C14.1103 5.32902 14.3011 5.25 14.5 5.25Z" />
      <path d="M9.5 17.25C9.69891 17.25 9.88968 17.329 10.0303 17.4697C10.171 17.6103 10.25 17.8011 10.25 18C10.25 18.1989 10.171 18.3897 10.0303 18.5303C9.88968 18.671 9.69891 18.75 9.5 18.75C9.30109 18.75 9.11032 18.671 8.96967 18.5303C8.82902 18.3897 8.75 18.1989 8.75 18C8.75 17.8011 8.82902 17.6103 8.96967 17.4697C9.11032 17.329 9.30109 17.25 9.5 17.25Z" />
      <path d="M9.5 11.25C9.69891 11.25 9.88968 11.329 10.0303 11.4697C10.171 11.6103 10.25 11.8011 10.25 12C10.25 12.1989 10.171 12.3897 10.0303 12.5303C9.88968 12.671 9.69891 12.75 9.5 12.75C9.30109 12.75 9.11032 12.671 8.96967 12.5303C8.82902 12.3897 8.75 12.1989 8.75 12C8.75 11.8011 8.82902 11.6103 8.96967 11.4697C9.11032 11.329 9.30109 11.25 9.5 11.25Z" />
      <path d="M9.5 5.25C9.69891 5.25 9.88968 5.32902 10.0303 5.46967C10.171 5.61032 10.25 5.80109 10.25 6C10.25 6.19891 10.171 6.38968 10.0303 6.53033C9.88968 6.67098 9.69891 6.75 9.5 6.75C9.30109 6.75 9.11032 6.67098 8.96967 6.53033C8.82902 6.38968 8.75 6.19891 8.75 6C8.75 5.80109 8.82902 5.61032 8.96967 5.46967C9.11032 5.32902 9.30109 5.25 9.5 5.25Z" />
    </IconStrokeBase>
  );
};

export const IconAlertDot = ({
  variant = "base",
  className,
  ...rest
}: IconProps) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 18 18"
      width={iconSize(variant)}
      height={iconSize(variant)}
      fill="none"
      className={className}
      aria-label="Alert"
      role="img"
      {...rest}
    >
      <title>Alert</title>
      <g filter="url(#alertDotGlow)">
        <circle cx="8.91425" cy="8.91431" r="3.61426" fill="#F9B513" />
      </g>
      <defs>
        <filter
          id="alertDotGlow"
          x="0"
          y="0"
          width="17.8285"
          height="17.8285"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feMorphology
            radius="1"
            operator="dilate"
            in="SourceAlpha"
            result="effect1_dropShadow"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="2.15" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.976471 0 0 0 0 0.709804 0 0 0 0 0.0745098 0 0 0 1 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  );
};

export const IconLightbulb = ({
  color,
  variant = "base",
  ...rest
}: IconProps) => {
  const subtle = useIsSubtle();
  const fill = color ?? (subtle ? SUBTLE_COLOR : "#111920");
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={iconSize(variant)}
      height={iconSize(variant)}
      fill="none"
      {...rest}
    >
      <title>Resources</title>
      <path
        d="M13.9268 20.8867C14.341 20.8867 14.6768 21.2225 14.6768 21.6367C14.6768 22.0509 14.341 22.3867 13.9268 22.3867H10.0723C9.65814 22.3866 9.32227 22.0509 9.32227 21.6367C9.32227 21.2226 9.65814 20.8868 10.0723 20.8867H13.9268ZM14.8906 17.0322C15.3048 17.0323 15.6406 17.3681 15.6406 17.7822C15.6405 18.1963 15.3047 18.5321 14.8906 18.5322H9.1084C8.69442 18.532 8.3585 18.1962 8.3584 17.7822C8.3584 17.3681 8.69436 17.0324 9.1084 17.0322H14.8906ZM12 1.61328C13.7323 1.61333 15.3942 2.30142 16.6191 3.52637C17.844 4.75134 18.5322 6.4132 18.5322 8.14551C18.5322 9.57149 17.9866 11.0225 16.8652 12.0479L16.8662 12.0488C16.1452 12.7699 15.7774 13.3171 15.626 14.0742C15.5448 14.4802 15.1502 14.744 14.7441 14.6631C14.338 14.5819 14.0741 14.1864 14.1553 13.7803C14.3892 12.6104 14.9853 11.8087 15.8057 10.9883L15.835 10.9609C16.6213 10.2531 17.0322 9.21277 17.0322 8.14551C17.0322 6.81093 16.5013 5.5306 15.5576 4.58691C14.614 3.64346 13.3344 3.11333 12 3.11328C10.6654 3.11328 9.3851 3.64323 8.44141 4.58691C7.49772 5.5306 6.96778 6.81093 6.96777 8.14551C6.9678 8.96233 7.11431 9.90923 8.19336 10.9883C8.91005 11.705 9.60846 12.604 9.84375 13.7803C9.92498 14.1864 9.66202 14.5818 9.25586 14.6631C8.84972 14.7443 8.45428 14.4804 8.37305 14.0742C8.2228 13.3234 7.76508 12.6811 7.13281 12.0488C5.70647 10.6225 5.4678 9.25596 5.46777 8.14551C5.46778 6.41311 6.15587 4.75136 7.38086 3.52637C8.60585 2.30138 10.2676 1.61328 12 1.61328Z"
        fill={fill}
      />
    </svg>
  );
};

export const IconHelp = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth={1.5} title="Help" {...props}>
      <path d="M4.93 4.93L9.17 9.17M14.83 14.83L19.07 19.07M19.07 4.93L14.83 9.17L18.36 5.64M4.93 19.07L9.17 14.83M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12ZM16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12Z" />
    </IconStrokeBase>
  );
};

export const IconBeaker = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth={1.5} title="Labs" {...props}>
      <path d="M10 2v7.527a2 2 0 0 1-.211.896L4.72 20.55a1 1 0 0 0 .9 1.45h12.76a1 1 0 0 0 .9-1.45l-5.069-10.127A2 2 0 0 1 14 9.527V2M8.5 2h7M7 16h10" />
    </IconStrokeBase>
  );
};

export const IconDocs = (props: IconProps) => {
  return (
    <IconStrokeBase strokeWidth={1.5} title="Docs" {...props}>
      <path d="M12 7C12 5.93913 11.5786 4.92172 10.8284 4.17157C10.0783 3.42143 9.06087 3 8 3H2V18H9C9.79565 18 10.5587 18.3161 11.1213 18.8787C11.6839 19.4413 12 20.2044 12 21M12 7V21M12 7C12 5.93913 12.4214 4.92172 13.1716 4.17157C13.9217 3.42143 14.9391 3 16 3H22V18H15C14.2044 18 13.4413 18.3161 12.8787 18.8787C12.3161 19.4413 12 20.2044 12 21" />
    </IconStrokeBase>
  );
};
